import { expValEquals, expValNoExposure } from '@confluence/feature-experiments';
import { useSessionData, useIsLicensed } from '@confluence/session-data';

import { useGetOnboardingState, deserializeState } from '../onboardingState/onboardingState';
import { NTH_USER_SETUP_CONTENT_TYPE } from '../../constants/onboarding-state/onboarding-constants';
import { isLocaleEnglish } from '../../utils/isLocaleEnglish';

export enum ConfluenceEdition {
	FREE = 'FREE',
	PREMIUM = 'PREMIUM',
	STANDARD = 'STANDARD',
}

type SelectedContentType = 'page' | 'live-doc' | 'whiteboard' | 'database';

type NthUserContentTypePageState = {
	showContentTypeScreen: boolean;
	hasSeenContentSpotlight: boolean;
	selectedContentType?: SelectedContentType;
	popularContentFound: boolean;
	landingContentId?: string;
	loading: boolean;
	error: Error | undefined;
};

/**
 * Checks whether to enable Nth User Content Type screen
 * If user is not eligible for the experiment, the screen will not be shown
 *
 * @returns {NthUserContentTypePageState} an object with boolean properties for whether to show the content type screen
 */
export function useIsNthUserContentTypePageEnabled({
	hasValidContinueUrl = false,
}: {
	hasValidContinueUrl?: boolean;
} = {}): NthUserContentTypePageState {
	const { edition, locale } = useSessionData();
	const isLicensed = useIsLicensed();

	const isFreeEdition = edition === ConfluenceEdition.FREE;
	const isPaidEdition = !isFreeEdition;
	const isEnglishLocale = isLocaleEnglish(locale);

	const isIneligibleUser =
		!isLicensed ||
		// temporary step until translations are complete
		!isEnglishLocale ||
		// user already has a landing path
		hasValidContinueUrl ||
		// skip queries if flag is not serving any cohorts
		(isFreeEdition &&
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
			expValNoExposure<string>(
				'cc_onboarding_content_type_nth_user_screen_free',
				'cohort',
				'not-enrolled',
			) === 'not-enrolled') ||
		(isPaidEdition &&
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
			expValNoExposure<string>(
				'cc_onboarding_content_type_nth_user_screen_paid',
				'cohort',
				'not-enrolled',
			) === 'not-enrolled');

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_SELECTED_CONTENT_TYPE,
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_SPOTLIGHT_SEEN,
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_POPULAR_CONTENT_FOUND,
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_LANDING_CONTENT_ID,
		],
		// skip query condition
		isIneligibleUser,
	);

	const {
		nthUserSelectedContentType,
		nthUserContentTypeSpotlightSeen,
		nthUserContentTypePopularContentFound,
		nthUserContentTypeLandingContentId,
	} = deserializeState(onboardingStateData);

	const loading = onboardingStateLoading;
	const error = onboardingStateError;

	if (loading || error || isIneligibleUser) {
		return {
			showContentTypeScreen: false,
			hasSeenContentSpotlight: true,
			selectedContentType: nthUserSelectedContentType,
			popularContentFound: false,
			landingContentId: '',
			loading,
			error,
		};
	}

	// fire exposures along with final check
	const showContentTypeScreen =
		(isFreeEdition &&
			expValEquals<string>(
				'cc_onboarding_content_type_nth_user_screen_free',
				'cohort',
				'experiment',
			)) ||
		(isPaidEdition &&
			expValEquals<string>(
				'cc_onboarding_content_type_nth_user_screen_paid',
				'cohort',
				'experiment',
			));

	return {
		showContentTypeScreen,
		hasSeenContentSpotlight: Boolean(nthUserContentTypeSpotlightSeen),
		selectedContentType: nthUserSelectedContentType,
		popularContentFound: Boolean(nthUserContentTypePopularContentFound),
		landingContentId: nthUserContentTypeLandingContentId,
		loading,
		error,
	};
}
